import React from "react";
import { motion } from "framer-motion";
import Container from "components/Container";
import CreateAccountModal from "components/CreateAccountModal";
import DownloadModal from "components/DownloadModal";
import MobileDownloadModal from "components/MobileDownloadModal";
import { useSignupForm } from "hooks/useSignupForm";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "components/TurnstileWidget";

import styles from "./styles.module.scss";

import LogoWinAZ from "assets/img-winaz/logo.webp";
import TypoWinAZ from "assets/img-winaz/txt.webp";
import BackgroundDesktop from "assets/img-winaz/background/bgngang2.webp";
import BackgroundMobile from "assets/img-winaz/background/bgdoc2.webp";
import BackgroundLight from "assets/img-winaz/bglight.webp";
import ButtonRegister from "assets/img-winaz/button/btndawng ki.webp";
import BannerAI from "assets/img-winaz/banner/bannerai2.webp";
import BannerCSKH from "assets/img-winaz/banner/bannercskh2.webp";
import LabelX3 from "assets/img-winaz/button/label29k.webp";
import BgBaoMat from "assets/img-winaz/bgbaomat.webp";
import LogoGalaxy from "assets/img-winaz/logogalaxy.webp";
import HeroBannerMobile1 from "assets/img-winaz/banner-moblie/bannerngang2-1.webp";
import HeroBannerMobile2 from "assets/img-winaz/banner-moblie/bannerngang2-2.webp";
import InputBackground from "assets/img-winaz/signup-form/input.webp";
import IconUser from "assets/img-winaz/signup-form/iconusser.webp";
import IconPassword from "assets/img-winaz/signup-form/iconpassword.webp";
import FormBackground from "assets/img-winaz/signup-form/bgpopup2.webp";
import SocialFacebook from "assets/img-winaz/social-icon/001-facebook.webp";
import SocialTelegram from "assets/img-winaz/social-icon/002-telegram.webp";
import { SOCIAL_LINKS } from "const";
import { flushEngagementThen } from "utils/dataLayer";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

type SliderItem = {
  id: number;
  src: string;
  alt: string;
};

type SocialLink = {
  id: string;
  icon: string;
  alt: string;
  url: string;
  target?: "_self" | "_blank";
};

const HERO_MOBILE_BANNERS: SliderItem[] = [
  { id: 0, src: HeroBannerMobile1, alt: "WinAZ khuyến mãi 1" },
  { id: 1, src: HeroBannerMobile2, alt: "WinAZ khuyến mãi 2" }
];

const SOCIAL_ITEMS: SocialLink[] = [
  {
    id: "facebook",
    icon: SocialFacebook,
    alt: "Facebook",
    url: SOCIAL_LINKS.facebook,
    target: "_blank",
  },
  {
    id: "telegram",
    icon: SocialTelegram,
    alt: "Telegram",
    url: SOCIAL_LINKS.telegram,
    target: "_blank",
  },
];

const INTERCOM_APP_ID = "snx821c4";

type IntercomModule = typeof import("@intercom/messenger-js-sdk");

const INTERACTIVE_IMAGE_PROPS = {
  initial: { filter: "brightness(1)", scale: 1 },
  whileHover: { filter: "brightness(1.08)", scale: 1.03 },
  whileTap: { scale: 0.96 },
  transition: {
    type: "spring" as const,
    stiffness: 450,
    damping: 32,
    mass: 0.28,
  },
};

const Home: React.FunctionComponent = () => {
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [isMobileDownloadModalOpen, setIsMobileDownloadModalOpen] = React.useState(false);
  const [mobilePlatform, setMobilePlatform] = React.useState<"android" | "ios">("android");
  const intercomModuleRef = React.useRef<Promise<IntercomModule | null> | null>(
    null
  );
  const turnstileWidgetRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const hasTrackedInlineFormStartRef = React.useRef(false);
  const usernameInputRef = React.useRef<HTMLInputElement | null>(null);
  const handleResetTurnstile = React.useCallback(() => {
    turnstileWidgetRef.current?.reset();
  }, []);
  const pushDataLayerEvent = React.useCallback(
    (eventName: string, payload: Record<string, any> = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...payload,
      });
    },
    []
  );
  const trackInlineFormStart = React.useCallback(() => {
    if (hasTrackedInlineFormStartRef.current) {
      return;
    }

    hasTrackedInlineFormStartRef.current = true;
    pushDataLayerEvent("lp_form_start", { form_id: "register_inline" });
  }, [pushDataLayerEvent]);
  const trackRegisterCta = React.useCallback(
    (location: string) => {
      pushDataLayerEvent("lp_click_cta_register", { location });
    },
    [pushDataLayerEvent]
  );
  const {
    username,
    password,
    apiError,
    usernameError,
    passwordError,
    isSubmitting,
    turnstileRequired,
    handleSubmit: handleInlineSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_inline",
    onTurnstileReset: handleResetTurnstile,
  });

  const loadIntercom = React.useCallback(() => {
    if (typeof window === "undefined") {
      return Promise.resolve(null);
    }

    if (!intercomModuleRef.current) {
      intercomModuleRef.current = import("@intercom/messenger-js-sdk")
        .then((module) => {
          module.default({
            app_id: INTERCOM_APP_ID,
          });
          return module;
        })
        .catch((error) => {
          console.error("Failed to load Intercom messenger", error);
          intercomModuleRef.current = null;
          return null;
        });
    }

    return intercomModuleRef.current;
  }, []);

  React.useEffect(() => {
    loadIntercom();
  }, [loadIntercom]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    HERO_MOBILE_BANNERS.forEach(({ src }) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleLogoClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_logo", { location: "header" });
  }, [pushDataLayerEvent]);

  const handleNavigate = React.useCallback(
    (url: string, target: "_self" | "_blank" = "_self") => {
      if (typeof window === "undefined") {
        return;
      }

      if (target === "_blank") {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      void flushEngagementThen(url);
    },
    []
  );

  const openCreateAccount = React.useCallback(() => {
    setIsCreateAccountModalOpen(true);
  }, []);

  const handleRegisterCtaClick = React.useCallback(
    (location: string) => {
      trackRegisterCta(location);
      openCreateAccount();
    },
    [openCreateAccount, trackRegisterCta]
  );

  const handleCloseCreateAccount = React.useCallback(() => {
    setIsCreateAccountModalOpen(false);
  }, []);

  const handleOpenDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(true);
  }, []);

  const handleCloseDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(false);
  }, []);

  const handleOpenMobileDownloadModal = React.useCallback((platform: "android" | "ios") => {
    setMobilePlatform(platform);
    setIsMobileDownloadModalOpen(true);
  }, []);

  const handleCloseMobileDownloadModal = React.useCallback(() => {
    setIsMobileDownloadModalOpen(false);
  }, []);

  const handleSocialClick = React.useCallback(
    (
      platformId: string,
      url: string,
      target: "_self" | "_blank" = "_self",
      location: "hero" | "desktop" | "footer"
    ) => {
      const eventName =
        platformId === "telegram" ? "lp_click_social_Telegram" : "lp_click_social_FB";
      pushDataLayerEvent(eventName, {
        social_platform: platformId,
        location,
      });
      handleNavigate(url, target);
    },
    [handleNavigate, pushDataLayerEvent]
  );

  return (
    <div className={styles.home}>
      <div className={styles.sliderViewport}>
        <img
          className={styles.heroBackgroundDesktop}
          src={BackgroundDesktop}
          alt=""
          aria-hidden="true"
        />
        <img
          className={styles.heroBackgroundMobile}
          src={BackgroundMobile}
          alt=""
          aria-hidden="true"
        />
        <div className={styles.sliderTrack}>
          <section
            id="section-hero"
            className={`${styles.slide} ${styles.hero}`}
          >
            <div className={styles.heroFigureWrapper}>
              <img className={styles.heroFigure} src={BackgroundLight} alt="" />
            </div>
            <Container className={styles.heroContainer}>
              <div className={styles.heroContent}>
                <button
                  type="button"
                  id="logo"
                  className={`${styles.logoButton} ${styles.logoPrimary}`}
                  onClick={handleLogoClick}
                  aria-label="WinAZ"
                >
                  <img src={LogoWinAZ} alt="WinAZ" />
                </button>

                <div className={styles.heroMobileBanner}>
                  {HERO_MOBILE_BANNERS.map((item) => (
                    <img
                      key={item.id}
                      src={item.src}
                      alt={item.alt}
                      className={styles.heroMobileBannerImage}
                    />
                  ))}
                </div>

                <div className={styles.heroFormCluster}>
                  <form
                    className={styles.loginCard}
                    style={{ backgroundImage: `url(${FormBackground})` }}
                    onSubmit={handleInlineSubmit}
                  >
                    <img
                      src={TypoWinAZ}
                      alt="WinAZ - Đẳng cấp thượng lưu"
                      className={styles.formHeaderImage}
                    />
                    <div className={styles.loginFields}>
                      <div className={styles.fieldGroup}>
                        <label
                          className={styles.inputWrapper}
                          style={{ backgroundImage: `url(${InputBackground})` }}
                        >
                          <img src={IconUser} alt="" aria-hidden="true" />
                          <input
                            ref={usernameInputRef}
                            type="text"
                            placeholder="Tên đăng nhập"
                            value={username}
                            onChange={handleUsernameChange}
                            onFocus={trackInlineFormStart}
                            disabled={isSubmitting}
                          />
                        </label>
                        {usernameError ? (
                          <div
                            className={styles.inlineError}
                            aria-live="assertive"
                          >
                            Vui lòng nhập tên đăng nhập
                          </div>
                        ) : null}
                      </div>
                      <div className={styles.fieldGroup}>
                        <label
                          className={styles.inputWrapper}
                          style={{ backgroundImage: `url(${InputBackground})` }}
                        >
                          <img src={IconPassword} alt="" aria-hidden="true" />
                          <input
                            type="password"
                            placeholder="Mật khẩu"
                            value={password}
                            onChange={handlePasswordChange}
                            onFocus={trackInlineFormStart}
                            disabled={isSubmitting}
                          />
                        </label>
                        {passwordError ? (
                          <div
                            className={styles.inlineError}
                            aria-live="assertive"
                          >
                            Vui lòng nhập mật khẩu
                          </div>
                        ) : null}
                      </div>
                      {turnstileRequired ? (
                        <div className={styles.turnstileGroup}>
                          <div className={styles.turnstileBlock}>
                            <div className={styles.turnstileInner}>
                              <TurnstileWidget
                                ref={turnstileWidgetRef}
                                className={styles.turnstileWidget}
                                onTokenChange={handleTurnstileTokenChange}
                              />
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {apiError ? (
                        <div
                          className={styles.inlineError}
                          aria-live="assertive"
                        >
                          {apiError}
                        </div>
                      ) : null}
                    </div>

                    <motion.button
                      type="submit"
                      className={styles.registerButtonWrapper}
                      disabled={isSubmitting}
                      {...INTERACTIVE_IMAGE_PROPS}
                      data-cta
                    >
                      <img src={LabelX3} alt="Nạp Đầu x3" className={styles.registerLabel} />
                      <img
                        src={ButtonRegister}
                        alt="Đăng ký"
                        className={styles.registerButtonImage}
                      />
                    </motion.button>
                  </form>
                </div>

                <div className={styles.securityBadge}>
                  <img src={BgBaoMat} alt="Công nghệ Blockchain, Thuật toán MD5 - Minh bạch hoàn toàn" />
                </div>


                <button
                  type="button"
                  className={styles.registerButtonMobile}
                  onClick={() => handleRegisterCtaClick("hero_mobile")}
                >
                  <motion.img
                    {...INTERACTIVE_IMAGE_PROPS}
                    src={ButtonRegister}
                    alt="Đăng ký"
                  />
                </button>
              </div>
            </Container>
          </section>

        </div>
      </div>

      <div className={styles.desktopSideBannersWrapper}>
        <div className={`${styles.desktopSideBanner} ${styles.desktopSideBannerLeft}`}>
          <img src={BannerAI} alt="AI WinAZ" className={styles.sideBannerImage} />

        </div>
        <div className={`${styles.desktopSideBanner} ${styles.desktopSideBannerRight}`}>
          <img src={BannerCSKH} alt="CSKH WinAZ" className={styles.sideBannerImage} />
        </div>
      </div>

      <div className={styles.desktopSocials} aria-label="Liên kết mạng xã hội">
        <div className={styles.desktopSocialsInner}>
          <img src={LogoGalaxy} alt="Galaxy Macau Entertainment" className={styles.galaxyLogo} />
          <p className={styles.galaxyText}>Một Sản Phẩm Thuộc Tập Đoàn Galaxy Macau Entertainment</p>
          <div className={styles.socialButtons}>
            {SOCIAL_ITEMS.map((item) => (
              <button
                key={item.id}
                id={`desktop-social-${item.id}`}
                type="button"
                className={`social-icon-${item.id}`}
                onClick={() =>
                  handleSocialClick(
                    item.id,
                    item.url,
                    item.target ?? "_self",
                    "desktop"
                  )
                }
              >
                <img src={item.icon} alt={item.alt} />
              </button>
            ))}
          </div>
        </div>
      </div>



      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={handleCloseDownloadModal}
      />

      <MobileDownloadModal
        isOpen={isMobileDownloadModalOpen}
        onClose={handleCloseMobileDownloadModal}
        platform={mobilePlatform}
      />

      <CreateAccountModal
        isOpen={isCreateAccountModalOpen}
        onClose={handleCloseCreateAccount}
      />
    </div>
  );
};

export default Home;
